import { Table, TableColumnsType } from 'antd';
import React from 'react';

import ResizableTable from '../../../foundation/components/resizable_table/ResizableTable';
import LoadMoreBtn from '../components/LoadMoreBtn';
import { ClinVarHotSpotVariantListEntry } from '../redux';
import type {
  ClinVarHotspotSummaryRow,
  ClinVarHotspotVariantLink,
} from './hooks';
import { useClinVarHotspotLogic } from './hooks';

const renderClinVarHotspotVariantLink = (
  _: unknown,
  record: ClinVarHotSpotVariantListEntry,
) => {
  if (!record?.variant_clinvar_id) {
    return record?.variant_name;
  }
  return (
    <a
      href={`https://www.ncbi.nlm.nih.gov/clinvar/variation/${record.variant_clinvar_id}`}
      target="_blank"
      rel="noopener noreferrer"
      className="interpretation__intervar-link__anchor"
    >
      {record.variant_name}
    </a>
  );
};

const renderClinVarHotspotSummaryLink = (record: ClinVarHotspotVariantLink) => {
  if (!record?.clinvar_id) {
    return record?.name;
  }
  return (
    <a
      href={`https://www.ncbi.nlm.nih.gov/clinvar/variation/${record.clinvar_id}`}
      target="_blank"
      rel="noopener noreferrer"
      className="interpretation__intervar-link__anchor"
    >
      {record.name}
    </a>
  );
};

const renderClinVarHotspotSummaryVariant = (
  value: ClinVarHotspotVariantLink,
  row: ClinVarHotspotSummaryRow,
) => (
  <>
    {row.flanking_region} {renderClinVarHotspotSummaryLink(value)}
  </>
);

const clinVarHotspotVariantsColumns: TableColumnsType<ClinVarHotSpotVariantListEntry> =
  [
    {
      title: 'Variant',
      key: 'variant_linkout',
      render: renderClinVarHotspotVariantLink,
    },
    {
      title: 'Classification',
      dataIndex: 'classification',
      key: 'classification',
      filters: [{ text: 'Pathogenic / Likely Pathogenic', value: 'p/lp' }],
      onFilter: (value, record) =>
        record.classification === 'Likely_pathogenic' ||
        record.classification === 'Pathogenic',
    },
    {
      title: 'Review Status',
      dataIndex: 'review_status',
      key: 'review_status',
    },
  ];

const clinVarHotspotColumns: TableColumnsType<ClinVarHotspotSummaryRow> = [
  {
    title: 'Next B/LB missense/inframe variant in gene',
    dataIndex: 'b_lb_variant',
    width: '38%',
    render: renderClinVarHotspotSummaryVariant,
  },
  {
    title: 'Distance',
    dataIndex: 'distance',
    width: '8%',
  },
  {
    title: 'All missense/inframe variants',
    dataIndex: 'missense_count',
    width: '14%',
  },
  {
    title: 'P/LP missense/inframe variants',
    dataIndex: 'missense_p_lp_count',
    width: '14%',
  },
  {
    title: 'Region size',
    dataIndex: 'region_size',
    width: '10%',
    onCell: (_, index) => ({
      rowSpan: index === 0 ? 2 : 0,
    }),
  },
  {
    title: 'Ratio P/LP to all missense/inframe variants',
    dataIndex: 'missense_p_lp_ratio',
    width: '16%',
    onCell: (_, index) => ({
      rowSpan: index === 0 ? 2 : 0,
    }),
  },
];

/**
 * Renders the ClinVar hotspot summary table (part of the "Other informative
 * variants" section, no title of its own) plus the expandable list of
 * ClinVar variants in the hotspot region via the chevron-down + "more"
 * pattern used in ClinObs. All data fetching and derived state live in
 * useClinVarHotspotLogic; this component only renders.
 */
const ClinVarHotspotSection = () => {
  const {
    clinVarHotspotData,
    isClinVarHotspotDataLoading,
    clinVarHotspotVariants,
    isClinVarHotspotVariantsLoading,
    clinVarHotspotDataSource,
    showMore,
    handleShowMoreClick,
  } = useClinVarHotspotLogic();

  if (!clinVarHotspotData) {
    return null;
  }

  return (
    <div className="molecular-impact__hotspot-section">
      <Table
        columns={clinVarHotspotColumns}
        dataSource={clinVarHotspotDataSource}
        pagination={false}
        size="small"
        loading={isClinVarHotspotDataLoading}
        tableLayout="fixed"
      />
      {showMore && (
        <ResizableTable<ClinVarHotSpotVariantListEntry>
          columns={clinVarHotspotVariantsColumns}
          dataSource={clinVarHotspotVariants?.variants}
          loading={isClinVarHotspotVariantsLoading}
          pagination={{
            pageSize: 200,
          }}
          size="small"
          rowClassName={(record) =>
            record.is_current_variant ? 'highlighted-clinvar-hotspot-row' : ''
          }
          persistenceKey="clinvar-hotspot-variants"
        />
      )}
      <LoadMoreBtn
        showMore={showMore}
        handleShowMoreClick={handleShowMoreClick}
      />
    </div>
  );
};

export default ClinVarHotspotSection;
